import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { map } from 'rxjs/operators';
import { 
  MenuPayloadDto, 
  MenuItemDto, 
  ProjectDto,
  ProjectChildDto,
  SidebarMenuItem 
} from '../models';
import { MENU_MOCK } from '../mocks';
import { STATIC_MENU_ITEMS, MENU_ICONS } from '../configs';

/**
 * Serwis odpowiedzialny za pobieranie i transformację menu sidebara.
 * 
 * Obecnie używa mocka, ale struktura jest przygotowana do podłączenia 
 * prawdziwego backendu - wystarczy podmienić metodę fetchMenuFromApi().
 */
@Injectable({
  providedIn: 'root'
})
export class SidebarMenuService {

  /**
   * Pobiera kompletne menu (statyczne + dynamiczne z backendu)
   * @returns Observable z tablicą elementów menu
   */
  getMenu(): Observable<SidebarMenuItem[]> {
    return this.fetchMenuFromApi().pipe(
      map(_payload => this.buildMenuFromPayload({items: []}))
    );
  }

  /**
   * Pobiera tylko statyczne elementy menu (bez backendu)
   */
  getStaticMenu(): SidebarMenuItem[] {
    return [...STATIC_MENU_ITEMS];
  }

  /**
   * Symulacja wywołania API - zwraca mock z opóźnieniem
   * 
   * TODO: Podmienić na prawdziwe wywołanie HTTP gdy backend będzie gotowy:
   * return this.http.get<MenuPayloadDto>(`${environment.apiUrl}menu`);
   */
  private fetchMenuFromApi(): Observable<MenuPayloadDto> {
    // Symulacja opóźnienia sieciowego (200ms)
    return of(MENU_MOCK).pipe(delay(200));
  }

  /**
   * Transformuje payload z backendu na strukturę menu UI
   */
  private buildMenuFromPayload(payload: MenuPayloadDto): SidebarMenuItem[] {
    const dynamicItems = payload.items?.map(item => this.mapItemToMenu(item)) || [];
    
    return [
      ...STATIC_MENU_ITEMS,
      ...dynamicItems
    ];
  }

  /**
   * Mapuje pojedynczy element z backendu (np. Akademia) na SidebarMenuItem
   */
  private mapItemToMenu(item: MenuItemDto): SidebarMenuItem {
    return {
      id: item.id,
      label: item.name,
      icon: MENU_ICONS.academy,
      route: `/academy/${item.slug ?? item.id}`,
      expanded: false,
      children: item.projects?.map(project => this.mapProjectToMenu(item, project)) || []
    };
  }

  /**
   * Mapuje projekt na SidebarMenuItem
   */
  private mapProjectToMenu(parent: MenuItemDto, project: ProjectDto): SidebarMenuItem {
    return {
      id: `${parent.id}:${project.id}`,
      label: project.name,
      icon: MENU_ICONS.project,
      route: `/academy/${parent.slug ?? parent.id}/project/${project.slug ?? project.id}`,
      children: project.children?.map(child => this.mapChildToMenu(parent, project, child)) || []
    };
  }

  /**
   * Mapuje element dziecka na SidebarMenuItem
   */
  private mapChildToMenu(
    academy: MenuItemDto, 
    project: ProjectDto, 
    child: ProjectChildDto
  ): SidebarMenuItem {
    return {
      id: `${academy.id}:${project.id}:${child.id}`,
      label: child.name,
      icon: MENU_ICONS.child,
      route: child.route ?? `/academy/${academy.slug ?? academy.id}/project/${project.slug ?? project.id}/${child.slug ?? child.id}`
    };
  }
}
